import type { Theme } from "@mui/material/styles";
import type { ActivityEntry } from "./timeline-types";

export interface EntryChipProps {
	label: string;
	color?: "success" | "info" | "primary" | "secondary" | "warning" | "error";
	customColor?: string;
	/** Shown on chip hover — e.g. the raw send error for an undeliverable email. */
	tooltip?: string;
}

/**
 * Get chip label and color based on entry type
 */
export function getEntryChipProps(entry: ActivityEntry, theme: Theme): EntryChipProps {
	switch (entry.type) {
		case "email_queued":
			return {
				label: entry.data.assignment_name
					? "Email Pitch Queued"
					: "Email Queued",
				color: "success",
			};
		case "email_sent": {
			// sent=True only means the queue row was processed — a non-null error
			// (e.g. smtp_rejected / probe timeout) means it never actually left.
			if (entry.data.error) {
				return {
					label: "Unable to send Email",
					color: "warning",
					tooltip: entry.data.error,
				};
			}
			if (entry.data.bounced) {
				return {
					label: entry.data.assignment_name
						? "Email Pitch Bounced"
						: "Email Bounced",
					color: "error",
				};
			}
			return {
				label: entry.data.assignment_name ? "Email Pitch Sent" : "Email Sent",
				color: "success",
			};
		}
		case "linkedin_queued":
			return {
				label: entry.data.assignment_name
					? "LinkedIn Pitch Queued"
					: "LinkedIn Queued",
				color: "primary",
			};
		case "linkedin_sent":
			return {
				label: entry.data.assignment_name
					? "LinkedIn Pitch Sent"
					: "LinkedIn Sent",
				color: "primary",
			};
		case "linkedin_conversation":
			return {
				label:
					entry.direction === "inbound"
						? "LinkedIn Reply"
						: "LinkedIn Message",
				color: "primary",
			};
		case "email_journal":
			return {
				label: entry.direction === "inbound" ? "Email Received" : "Email Sent",
				color: "success",
			};
		case "vote":
			return { label: "Vote", color: "secondary" };
		case "event":
			return { label: entry.data.name, color: "info" };
		case "board_movement":
			return {
				label: entry.fromColumn ? "Moved on Board" : "Added to Board",
				customColor: theme.palette.board.main,
			};
		case "note":
			return { label: "Note", color: "warning" };
		case "attachment":
			return { label: "File Uploaded", color: "warning" };
		case "call": {
			const direction =
				entry.data.call_direction === "inbound" ? "Inbound" : "Outbound";
			return { label: `${direction} Call`, color: "info" };
		}
		case "meeting": {
			const meetingType =
				entry.data.meeting_type === "video" ? "Video" : "In-Person";
			return { label: `${meetingType} Meeting`, color: "secondary" };
		}
		case "cooldown": {
			const isActive = new Date(entry.data.until) >= new Date();
			return {
				label: isActive ? "Cooldown Set" : "Cooldown Expired",
				color: "info",
			};
		}
	}
}
